/* eslint-disable @next/next/no-img-element */
import { datadogRum } from "@datadog/browser-rum";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import {
  isSeasonalDiscountActive,
  iteratablePricingOptions,
  personalPricingOptions,
  seasonalDiscountCode,
} from "data/variables";
import { useAtom } from "jotai";
import { useSearchParams } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { priceVariantAtom } from "src/atoms/pricingPage";
import { useUser } from "src/context/user";
import { http } from "src/http";
import { CONFIG, isDev } from "src/lib/config";
import { getRewardfulReferralData } from "src/lib/rewardful";
import styled from "styled-components";
import { API, StripeCheckoutSessionBody } from "../../api";
import { gtag_report_purchase } from "../../lib/ga";
import { UrlParser } from "../../util";
import StripeLogo from "./stripeLogo";
import SubscriptionCards from "./subscriptionCard";

const StyledForm = styled.form`
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  label {
    display: flex;
    flex-direction: column;
    text-transform: uppercase;
    font-size: 12px;
    font-weight: 700;
    input {
      margin-top: 0;
      margin-bottom: 0.5rem;
    }
  }
`;

const StyledNumber = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 40px;
  height: 40px;
  width: 40px;
  border: 3px solid var(--text-color);
  flex: 0 0 40px;
`;

const StyledPaymentButton = styled.button`
  flex: 1;
  width: 100%;
  max-width: 500px;
  background: var(--background-color);
  color: #000;
  display: flex;
  align-items: center;
  padding: 0.5rem;
  justify-content: space-between;
  outline: none;
  border: none;
  border-radius: 5px;
  margin-bottom: 1rem;
  cursor: pointer;
  img {
    height: 40px;
  }
  svg {
    height: 40px;
  }
`;

const StyledStepWrapper = styled.div`
  display: flex;
  margin: 2rem 0;
  gap: 2rem;
  flex: 1;
  @media screen and (max-width: 768px) {
    flex-direction: column;
  }
`;

interface StyledContainerProps {
  "data-active"?: boolean;
  "data-disabled"?: boolean;
}

const StyledContainer = styled.button<StyledContainerProps>`
  border: 1px solid ${(props) => (props["data-active"] ? "#5a87f5" : "#666")};
  padding: 1.5rem 2rem;
  width: 240px;
  border-radius: 1rem;
  font-size: 14px;
  background: transparent;
  text-align: left;
  line-height: 1.5;
  cursor: pointer;
  color: ${(props) => (props["data-active"] ? "var(--text-color)" : "#7c7c7c")};
  background-color: ${(props) => props["data-active"] && "var(--raised-background-color-two)"};
  &:hover {
    background-color: var(--raised-background-color-two);
  }
  h3 {
    display: flex;
    font-size: 18px;
    line-height: 22px;
    margin: 0 0 1rem 0;
    align-items: flex-start;
    svg {
      margin-right: 10px;
      flex-shrink: 0;
    }
    svg,
    path {
      fill: ${(props) => (props["data-active"] ? "var(--text-color)" : "#7c7c7c")};
    }
  }
  ${(props) =>
    props["data-disabled"] &&
    `
    border-image: linear-gradient(45deg, #306AFF, #8B30FF) 1;
  `}
`;
const StyledRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  width: 100%;
  margin-top: 2rem;
`;

const Check = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="#378D35" viewBox="0 0 24 24">
    <path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm-1 17l-5-5.299 1.399-1.43 3.574 3.736 6.572-7.007 1.455 1.403-8 8.597z" />
  </svg>
);

const Arrow = () => (
  <svg
    clipRule="evenodd"
    fillRule="evenodd"
    strokeLinejoin="round"
    strokeMiterlimit="2"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="m14.523 18.787s4.501-4.505 6.255-6.26c.146-.146.219-.338.219-.53s-.073-.383-.219-.53c-1.753-1.754-6.255-6.258-6.255-6.258-.144-.145-.334-.217-.524-.217-.193 0-.385.074-.532.221-.293.292-.295.766-.004 1.056l4.978 4.978h-14.692c-.414 0-.75.336-.75.75s.336.75.75.75h14.692l-4.979 4.979c-.289.289-.286.762.006 1.054.148.148.341.222.533.222.19 0 .378-.072.522-.215z"
      fillRule="nonzero"
    />
  </svg>
);

interface PaypalMessageState {
  message: null | string;
  error: string | null;
}
const Pro = () => {
  const [priceVariant] = useAtom(priceVariantAtom);

  const { refreshUser, userState } = useUser();

  const { register, handleSubmit, reset } = useForm({
    defaultValues: { email: userState.user?.email || "" },
  });

  const searchParams = useSearchParams();
  const parsedUrl = useMemo(() => new UrlParser(searchParams, ["affiliateId"]), [searchParams]);
  const params = parsedUrl.extractParamData(["affiliateId"]);

  const [paypalMessage, setPaypalMessage] = useState<PaypalMessageState>({
    message: null,
    error: null,
  });

  const setPpMessage = useCallback((newState: Partial<PaypalMessageState>) => {
    setPaypalMessage((s) => ({
      ...s,
      ...newState,
    }));
  }, []);

  const [step, setStep] = useState(2);

  const [paymentOption, setPaymentOption] = useState("");

  useEffect(() => {
    reset({ email: userState.user?.email || "" });
  }, [userState.user, reset]);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const onSubmit = () => {
    setStep(2);
  };

  const posthog = usePostHog();

  const handleStripeClick = async () => {
    if (!priceVariant) {
      console.error("priceVariant doesn't exists.");
      return;
    }

    const body: StripeCheckoutSessionBody = {
      priceVariant,
    };

    if (process.env.NEXT_PUBLIC_DEV_BRAZIL_REDIRECT_ORIGIN && isDev()) {
      body["redirectOrigin"] = process.env.NEXT_PUBLIC_DEV_BRAZIL_REDIRECT_ORIGIN;
    }
    const referralData = getRewardfulReferralData();
    if (referralData) {
      body["referralData"] = referralData;
    }

    if (params) {
      body["affiliateId"] = params.affiliateId;
    }

    if (isSeasonalDiscountActive && seasonalDiscountCode) {
      body["discountCode"] = seasonalDiscountCode;
    }

    const res = await API.createStripeCheckoutSession(body);

    datadogRum.addAction("clicked-stripe", {
      id: userState.user?.id,
      email: userState.user?.email,
      hasPremium: !!userState?.user?.subscription?.isActive,
    });

    posthog.capture("clicked-stripe", {
      user_id: userState.user?.id,
      email: userState.user?.email,
      has_premium: !!userState?.user?.subscription?.isActive,
    });

    if (res?.data?.data?.checkoutSessionUrl) {
      window.location.href = res?.data?.data?.checkoutSessionUrl;
    }
  };

  const paypalDisabled = !!iteratablePricingOptions.find((x) => x.priceVariant === priceVariant)
    ?.disablePaypal;

  return (
    <>
      <StyledForm
        onSubmit={handleSubmit(onSubmit)}
        style={{ display: "flex", flexDirection: "column", width: "100%" }}
      >
        <StyledStepWrapper>
          {step > 1 ? <Check /> : <StyledNumber>1</StyledNumber>}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <h2 style={{ margin: "6px 0 0 0" }}>Choose subscription</h2>
            <StyledRow>
              <SubscriptionCards isSeasonalDiscountActive={isSeasonalDiscountActive} />
            </StyledRow>
          </div>
        </StyledStepWrapper>
      </StyledForm>

      <StyledStepWrapper>
        {step > 2 ? <Check /> : <StyledNumber>2</StyledNumber>}
        <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
          <h2 style={{ margin: "6px 0 0rem 0" }}>Choose payment method</h2>
          {isSeasonalDiscountActive && seasonalDiscountCode && (
            <p className="text-lg">
              Use code <span className="font-bold text-brand-blue">{seasonalDiscountCode}</span> for{" "}
              <span className="h-[28px] w-[100px] items-center justify-center rounded-xl bg-yellow-400 px-2 font-bold text-black">
                25% OFF
              </span>
              <span> &#8212; Stripe only</span>
            </p>
          )}
          {step === 2 && (
            <div style={{ display: "flex", flexDirection: "column" }}>
              <StyledPaymentButton
                data-action-name="Purchase Flow: Stripe Button"
                onClick={() => {
                  handleStripeClick();
                }}
              >
                <div style={{ display: "flex", gap: "1rem" }}>
                  <StripeLogo />
                  <img src="/images/cards.png" alt="Pay with credit card" />
                  <div
                    className={`${isSeasonalDiscountActive ? "flex" : "hidden"} mt-3 h-5 w-14 items-center justify-center rounded-xl bg-brand-blue text-center font-bold text-white`}
                  >
                    -25%
                  </div>
                </div>
                <Arrow />
              </StyledPaymentButton>
              <StyledPaymentButton
                disabled={paypalDisabled}
                style={{
                  cursor: `${paypalDisabled ? "not-allowed" : "pointer"}`,
                  opacity: `${paypalDisabled ? 0.5 : 1}`,
                }}
                onClick={() => {
                  setPaymentOption("paypal");
                }}
              >
                <img src="/images/paypal.png" alt="Pay with Paypal" />
                {paypalDisabled ? (
                  <div
                    className={`flex items-center justify-center text-center font-semibold text-red-100 opacity-50`}
                  >
                    Not supported for lifetime plans
                  </div>
                ) : null}
                <Arrow />
              </StyledPaymentButton>
              {paymentOption === "paypal" && (
                <>
                  {userState.user?.subscription?.type === "PAYPAL" &&
                  userState.user?.subscription?.status === "ACTIVE" ? (
                    <p className="my-2 text-red-500">
                      You seem to already have a PayPal subscription, please contact us for support
                      on Discord.
                    </p>
                  ) : (
                    <>
                      {paypalMessage.error && (
                        <p className="my-2 text-red-500">{paypalMessage.error}</p>
                      )}
                      {paypalMessage.message && (
                        <p className="my-2 text-blue-200">{paypalMessage.message}</p>
                      )}
                      <PayPalScriptProvider
                        options={{
                          clientId: CONFIG.paypalClientId,
                          enableFunding: "card",
                          disableFunding: "",
                          dataSdkIntegrationSource: "integrationbuilder_sc",
                          vault: "true",
                          intent: "subscription",
                        }}
                      >
                        <PayPalButtons
                          style={{
                            shape: "rect",

                            layout: "vertical",
                          }}
                          forceReRender={[priceVariant]}
                          createSubscription={async (ppData, actions) => {
                            if (!priceVariant) {
                              console.error(`Price variant doesn't exist`);
                              return;
                            }
                            try {
                              const body: Record<string, unknown> = {
                                priceVariant,
                              };
                              if (params) {
                                body["affiliateId"] = params.affiliateId;
                              }

                              const response = await http.request({
                                url: "/api/paypal/subscriptions/create",
                                method: "POST",
                                headers: {
                                  "Content-Type": "application/json",
                                },

                                data: JSON.stringify(body),
                              });

                              const data = response.data;
                              const paypalResponse = data?.data?.paypalResponse;

                              if (paypalResponse?.id) {
                                console.log(`Successful subscription...`);

                                return paypalResponse.id;
                              } else {
                                console.error(
                                  { callback: "createSubscription", serverResponse: data },

                                  JSON.stringify(data, null, 2),
                                );

                                // (Optional) The following hides the button container and shows a message about why checkout can't be initiated

                                const errorDetail = paypalResponse?.details?.[0];

                                console.log(
                                  `Could not initiate PayPal Subscription...<br><br>${
                                    errorDetail?.issue || ""
                                  } ${errorDetail?.description || paypalResponse?.message || ""} ` +
                                    (paypalResponse?.debug_id
                                      ? `(${paypalResponse.debug_id})`
                                      : ""),

                                  { hideButtons: true },
                                );
                              }
                            } catch (error) {
                              console.error(error);
                              setPpMessage({
                                error: "Error initializing paypal subscription",
                              });
                            }
                          }}
                          onError={(err) => {
                            console.error("Got an error: ", err);
                          }}
                          onApprove={async (paypalData, actions) => {
                            if (!paypalData.orderID || !paypalData.subscriptionID) {
                              setPpMessage({
                                error: `Failed to activate the subscription: ${paypalData.subscriptionID}`,
                              });
                              return;
                            }
                            setPpMessage({
                              message: `You have successfully subscribed to Redact Premium. Your subscription id is: ${paypalData.subscriptionID}`,
                            });

                            try {
                              const response = await http.request({
                                url: "/api/paypal/subscriptions/confirm",
                                method: "POST",
                                headers: {
                                  "Content-Type": "application/json",
                                },
                                data: JSON.stringify({
                                  subscriptionId: paypalData.subscriptionID,
                                }),
                              });

                              gtag_report_purchase(
                                "paypal",
                                personalPricingOptions.premiumYear.fullPriceInt,
                              );

                              posthog?.capture("purchase", {
                                user_id: userState.user?.id,
                                email: userState.user?.email,
                                value: personalPricingOptions.premiumYear.fullPriceInt,
                                currency: "USD",
                                method: "paypal",
                              });

                              const data = response.data;
                              console.log("New subscription status:", data.subscriptionStatus);
                              refreshUser();
                            } catch (error) {
                              console.error("Error confirming subscription", error);
                              setPpMessage({ message: "Error confirming subscription" });
                            }
                          }}
                        />
                      </PayPalScriptProvider>
                    </>
                  )}
                </>
              )}
            </div>
          )}
          {step > 2 && paymentOption && <div>{paymentOption}</div>}
        </div>
      </StyledStepWrapper>

      <StyledStepWrapper>
        {step > 3 ? <Check /> : <StyledNumber>3</StyledNumber>}
        <div>
          <h2 style={{ margin: "6px 0 1rem 0" }}>Confirmation</h2>
        </div>
      </StyledStepWrapper>
    </>
  );
};

export default Pro;
